import { jsx } from 'react/jsx-runtime';
import { useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { useApi, errorApiRef, useApiHolder } from '@backstage/core-plugin-api';
import { commonProvider } from './commonProvider.esm.js';
import { guestProvider } from './guestProvider.esm.js';
import { customProvider } from './customProvider.esm.js';
import { IdentityApiSignOutProxy } from './IdentityApiSignOutProxy.esm.js';
import { useSearchParams } from 'react-router-dom';
import { useMountEffect } from '@react-hookz/web';
import { ForwardedError } from '@backstage/errors';
import { coreComponentsTranslationRef } from '../../translation.esm.js';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

const PROVIDER_STORAGE_KEY = "@backstage/core:SignInPage:provider";
const signInProviders = {
  guest: guestProvider,
  custom: customProvider,
  common: commonProvider
};
function validateIDs(id, providers) {
  if (id in providers)
    throw new Error(
      `"${id}" ID is duplicated. IDs of identity providers have to be unique.`
    );
}
function getSignInProviders(identityProviders) {
  const providers = identityProviders.reduce(
    (acc, config) => {
      if (typeof config === "string") {
        validateIDs(config, acc);
        acc[config] = { components: signInProviders[config], id: config };
        return acc;
      }
      const { id } = config;
      validateIDs(id, acc);
      acc[id] = { components: signInProviders.common, id, config };
      return acc;
    },
    {}
  );
  return providers;
}
const useSignInProviders = (providers, onSignInSuccess) => {
  const errorApi = useApi(errorApiRef);
  const apiHolder = useApiHolder();
  const [loading, setLoading] = useState(true);
  const { t } = useTranslationRef(coreComponentsTranslationRef);
  const [searchParams, _setSearchParams] = useSearchParams();
  useMountEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      errorApi.post(
        new ForwardedError(t("signIn.loginFailed"), new Error(errorParam))
      );
    }
  });
  const handleWrappedResult = useCallback(
    (identityApi) => {
      onSignInSuccess(
        IdentityApiSignOutProxy.from({
          identityApi,
          signOut: async () => {
            localStorage.removeItem(PROVIDER_STORAGE_KEY);
            await identityApi.signOut?.();
          }
        })
      );
    },
    [onSignInSuccess]
  );
  useLayoutEffect(() => {
    if (!loading) {
      return void 0;
    }
    const selectedProviderId = localStorage.getItem(
      PROVIDER_STORAGE_KEY
    );
    if (selectedProviderId === null) {
      setLoading(false);
      return void 0;
    }
    const provider = providers[selectedProviderId];
    if (!provider) {
      setLoading(false);
      return void 0;
    }
    let didCancel = false;
    provider.components.loader(apiHolder, provider.config?.apiRef).then((result) => {
      if (didCancel) {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        return;
      }
      if (result) {
        handleWrappedResult(result);
      } else {
        setLoading(false);
      }
    }).catch((error) => {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      if (didCancel) {
        return;
      }
      errorApi.post(error);
      setLoading(false);
    });
    return () => {
      didCancel = true;
    };
  }, [
    loading,
    errorApi,
    onSignInSuccess,
    apiHolder,
    providers,
    handleWrappedResult
  ]);
  const elements = useMemo(
    () => Object.keys(providers).map((key) => {
      const provider = providers[key];
      const { Component } = provider.components;
      const handleSignInSuccess = (result) => {
        handleWrappedResult(result);
      };
      const handleSignInStarted = () => {
        localStorage.setItem(
          PROVIDER_STORAGE_KEY,
          provider?.config?.id || provider.id
        );
      };
      const handleSignInFailure = () => {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
      };
      return /* @__PURE__ */ jsx(
        Component,
        {
          config: provider.config,
          onSignInStarted: handleSignInStarted,
          onSignInSuccess: handleSignInSuccess,
          onSignInFailure: handleSignInFailure
        },
        provider.id
      );
    }),
    [providers, handleWrappedResult]
  );
  return [loading, elements];
};

export { getSignInProviders, useSignInProviders };
//# sourceMappingURL=providers.esm.js.map
